const Sites = () => lazyLoadView(import('@views/sites'));
const Admin = () => lazyLoadView(import('@views/settings'));
const SharedLinks = () => lazyLoadView(import('@views/shared-links'));

const Builds = () => lazyLoadView(import('@views/home'));
const Create = () => lazyLoadView(import('@views/create'));
const Edit = () => lazyLoadView(import('@views/edit'));


const Build = () => lazyLoadView(import('@views/build'));
const BuildVersion = () => lazyLoadView(import('@views/build-version'));
const BuildShare = () => lazyLoadView(import('@views/build-share'));
const BuildSingle = () => lazyLoadView(import('@views/build-single'));
const BuildSingleVersion = () => lazyLoadView(import('@views/build-single-version'));
const BuildTeamcity = () => lazyLoadView(import('@views/build-teamcity'));

const Login = () => lazyLoadView(import('@views/login'));
const AuthCallback = () => lazyLoadView(import('@views/callback'));

export default [
  {
    path: '/',
    name: 'index',
    component: Sites,
    meta: {
      authRequired: true,
    }
  },
  {
    path: '/auth_callback',
    name: 'auth_callback',
    component: AuthCallback,
    meta: {
      authRequired: false,
      skipAuthorized: true,
    }
  },
  {
    path: '/login',
    name: 'login',
    component: Login,
    meta: {
      authRequired: false,
      skipAuthorized: true,
    },
  },
  {
    path: '/admin',
    name: 'admin',
    component: Admin,
    meta: {
      authRequired: true,
      authPermission: 'admin',
    }
  },
  {
    path: '/sentry',
    name: 'sentry',
    component: () => lazyLoadView(import('@views/test-sentry')),
  },
  {
    path: '/404',
    name: '404',
    component: require('@views/_404').default,
    // Allows props to be passed to the 404 page through route
    // params, such as `resource` to define what wasn't found.
    props: true,
    meta: {
      authRequired: false,
    }
  },
  {
    path: '/builds/tc/:teamcity_id',
    name: 'teamcity-build',
    component: BuildTeamcity,
    meta: {
      authRequired: true,
    }
  },
  {
    path: '/:project/builds/tc/:teamcity_id',
    redirect: {name: 'teamcity-build'},
  },
  {
    path: '/builds/:build_id/versions/:version_id',
    name: 'single-version-build',
    component: BuildSingleVersion,
    meta: {
      authRequired: true,
    }
  },
  {
    path: '/build/:build_id/versions/:version_id',
    redirect: {name: 'single-version-build'},
  },
  {
    path: '/builds/:build_id',
    name: 'single-build',
    component: BuildSingle,
    meta: {
      authRequired: true,
    }
  },
  {
    path: '/build/:build_id',
    redirect: {name: 'single-build'},
  },
  {
    path: '/:project',
    name: 'builds',
    component: Builds,
    meta: {
      authRequired: true,
    }
  },
  {
    path: '/:project/create',
    name: 'create',
    component: Create,
    props: ({ params }) => ({
      site: params.project,
    }),
    meta: {
      authRequired: true,
      authPermission: 'write',
    }
  },
  {
    path: '/:project/shared',
    name: 'shared-links',
    component: SharedLinks,
    meta: {
      authRequired: true,
      authPermission: 'admin',
    }
  },
  {
    path: '/:project/:hash',
    name: 'share-build',
    component: BuildShare,
    meta: {
      authRequired: false,
    }
  },
  {
    path: '/:project/builds/:build_id',
    name: 'build',
    component: Build,
    meta: {
      authRequired: true,
    }
  },
  {
    path: '/:project/builds/:build_id/versions/:id',
    name: 'version-build',
    component: Build,
    meta: {
      authRequired: true,
    }
  },
  {
    path: '/:project/builds/:build_id/edit',
    redirect: {name: 'edit'}
  },
  {
    path: '/builds/:build_id/edit',
    name: 'edit',
    component: Edit,
    meta: {
      authRequired: true,
    }
  },
  // Redirect any unmatched routes to the 404 page. This may
  // require some server configuration to work in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  {
    path: '*',
    redirect: '404',
    meta: {
      authRequired: false
    }
  },
]

// Lazy-loads view components, but with better UX. A loading view
// will be used if the component takes a while to load, falling
// back to a timeout view in case the page fails to load. You can
// use this component to lazy-load a route with:
//
// component: () => lazyLoadView(import('@views/my-view'))
//
// NOTE: Components loaded with this strategy DO NOT have access
// to in-component guards, such as beforeRouteEnter,
// beforeRouteUpdate, and beforeRouteLeave. You must either use
// route-level guards instead or lazy-load the component directly:
//
// component: () => import('@views/my-view')
//
function lazyLoadView(AsyncView) {
  const AsyncHandler = () => ({
    component: AsyncView,
    // A component to use while the component is loading.
    loading: require('@views/_loading').default,
    // Delay before showing the loading component.
    // Default: 200 (milliseconds).
    delay: 400,
    // A fallback component in case the timeout is exceeded
    // when loading the component.
    error: require('@views/_timeout').default,
    // Time before giving up trying to load the component.
    // Default: Infinity (milliseconds).
    timeout: 10000,
  })

  return Promise.resolve({
    functional: true,
    render(h, { data, children }) {
      // Transparently pass any props or children
      // to the view component.
      return h(AsyncHandler, data, children)
    },
  })
}
